import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { UserPlus, Edit, Trash2, Save, X, Shield, Building, Info, ChevronDown, ChevronUp, CheckCircle, MinusCircle, Eye } from 'lucide-react';
import { getAllUsers, createUser, updateUser, deleteUser, getStructures } from '../../api';
import { useImpersonate } from '../../contexts/ImpersonateContext';

const ACCESS_MATRIX = [
  {
    role: 'Admin Global',
    badge: 'bg-red-100 text-red-800',
    modules: { stocks: 'full', traceability: 'full', equipment: 'full', creche: 'full', tasks: 'full', diapers: 'full' },
    structures: 'Toutes',
    description: 'Accès complet à tout — CRUD + administration',
  },
  {
    role: 'Admin Structure',
    badge: 'bg-primary/10 text-primary-dark',
    modules: { stocks: 'selon', traceability: 'selon', equipment: 'selon', creche: 'selon', tasks: 'selon', diapers: 'selon' },
    structures: 'Assignées',
    description: 'CRUD complet sur ses structures et modules assignés',
  },
  {
    role: 'Équipe (Staff)',
    badge: 'bg-blue-100 text-blue-800',
    modules: { stocks: 'selon', traceability: 'selon', equipment: 'selon', creche: 'selon', tasks: 'selon', diapers: 'selon' },
    structures: 'Assignées',
    description: 'Créer et modifier (pas supprimer) sur ses modules assignés',
  },
  {
    role: 'Lecteur',
    badge: 'bg-gray-100 text-gray-800',
    modules: { stocks: 'selon', traceability: 'selon', equipment: 'selon', creche: 'selon', tasks: 'selon', diapers: 'selon' },
    structures: 'Assignées',
    description: 'Lecture seule sur ses modules assignés',
  },
];

const MODULE_ACCESS_LABELS = { full: 'Tout', selon: 'Selon modules', none: 'Aucun' };

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showAccessInfo, setShowAccessInfo] = useState(false);
  const { enabled: impersonateEnabled, startImpersonating } = useImpersonate();
  const ALL_MODULES = ['stocks', 'traceability', 'equipment', 'creche', 'creche_previsionnel', 'tasks', 'diapers', 'recruitment', 'timesheets', 'calendar', 'communication', 'hr', 'hr_dashboard', 'hr_formations', 'hr_documents', 'hr_visites_medicales', 'hr_transport_collection'];

  const HR_DOSSIERS_SUB_MODULES = ['hr_formations', 'hr_documents', 'hr_visites_medicales'];

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'structure_admin',
    modules: [...ALL_MODULES],
    structures: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, structuresRes] = await Promise.all([
        getAllUsers(),
        getStructures()
      ]);
      setUsers(usersRes.data);
      setStructures(structuresRes.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await updateUser(editingUser.id, formData);
      } else {
        await createUser(formData);
      }
      await fetchData();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }
    try {
      await deleteUser(userId);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      modules: Array.isArray(user.modules) ? user.modules : [...ALL_MODULES],
      structures: user.structures?.map(s => s.structureId) || [],
      defaultModule: user.defaultModule || '',
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'structure_admin',
      modules: [...ALL_MODULES],
      structures: [],
      defaultModule: '',
    });
    setEditingUser(null);
    setShowAddForm(false);
  };

  const toggleModule = (module) => {
    setFormData(prev => {
      const toggled = prev.modules.includes(module)
        ? prev.modules.filter(m => m !== module)
        : [...prev.modules, module];
      // hr_dossiers est géré automatiquement : actif si au moins un sous-module Dossiers est coché
      const hasDossierSub = HR_DOSSIERS_SUB_MODULES.some(m => toggled.includes(m));
      const withDossiers = hasDossierSub
        ? toggled.includes('hr_dossiers') ? toggled : [...toggled, 'hr_dossiers']
        : toggled.filter(m => m !== 'hr_dossiers');
      return { ...prev, modules: withDossiers };
    });
  };

  const toggleStructure = (structureId) => {
    setFormData(prev => ({
      ...prev,
      structures: prev.structures.includes(structureId)
        ? prev.structures.filter(id => id !== structureId)
        : [...prev.structures, structureId]
    }));
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Admin Global', color: 'bg-red-100 text-red-800' },
      structure_admin: { label: 'Admin Structure', color: 'bg-primary/10 text-primary-dark' },
      staff: { label: 'Équipe', color: 'bg-blue-100 text-blue-800' },
      viewer: { label: 'Lecteur', color: 'bg-gray-100 text-gray-800' }
    };
    const badge = badges[role] || badges.viewer;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const MODULE_LABELS = {
    stocks: 'Stocks & Commandes',
    traceability: 'Traçabilité',
    equipment: 'Équipements & Maintenance',
    creche: 'Crèche (Places & CA)',
    creche_previsionnel: 'Crèche — CA prévisionnel (confidentiel)',
    tasks: 'Tâches',
    diapers: 'Couches',
    recruitment: 'Recrutement',
    timesheets: 'Feuilles de temps',
    calendar: 'Calendrier',
    communication: 'Communication',
    hr: 'Employés',
    hr_dashboard: 'Dashboard RH',
    hr_formations: 'Dossiers RH — Formations',
    hr_documents: 'Dossiers RH — Documents',
    hr_visites_medicales: 'Dossiers RH — Visites médicales',
    hr_transport_collection: 'Collecte transport',
  };

  if (loading) {
    return <div className="p-4 text-center">Chargement...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-3xl font-bold">Gestion des utilisateurs</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAccessInfo(!showAccessInfo)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition ${
              showAccessInfo
                ? 'bg-primary/10 border-primary text-primary'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary hover:text-primary'
            }`}
            title="Tableau des accès rôles × modules"
          >
            <Info size={16} />
            <span className="hidden sm:inline">Accès</span>
            {showAccessInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Tableau des accès rôles × modules */}
      {showAccessInfo && (
        <div className="mb-6 bg-white rounded-xl border border-primary/20 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-primary/5 border-b border-primary/10">
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-primary" />
              <span className="font-semibold text-gray-800 text-sm">Tableau des accès — Rôles × Modules</span>
            </div>
            <button onClick={() => setShowAccessInfo(false)} className="text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2 font-semibold">Rôle</th>
                  <th className="px-4 py-2 font-semibold text-center">Stocks</th>
                  <th className="px-4 py-2 font-semibold text-center">Traça</th>
                  <th className="px-4 py-2 font-semibold text-center">Équip.</th>
                  <th className="px-4 py-2 font-semibold text-center">Crèche</th>
                  <th className="px-4 py-2 font-semibold text-center">Tâches</th>
                  <th className="px-4 py-2 font-semibold text-center">Couches</th>
                  <th className="px-4 py-2 font-semibold text-center">Structures</th>
                  <th className="px-4 py-2 font-semibold hidden lg:table-cell">Permissions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ACCESS_MATRIX.map((row) => (
                  <tr key={row.role} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.badge}`}>
                        {row.role}
                      </span>
                    </td>
                    {['stocks', 'traceability', 'equipment', 'creche', 'tasks', 'diapers'].map((mod) => (
                      <td key={mod} className="px-4 py-3 text-center">
                        {row.modules[mod] === 'full' ? (
                          <CheckCircle size={16} className="text-green-500 mx-auto" />
                        ) : row.modules[mod] === 'selon' ? (
                          <span className="text-xs text-gray-500 italic">si assigné</span>
                        ) : (
                          <MinusCircle size={16} className="text-gray-300 mx-auto" />
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center text-xs text-gray-600">{row.structures}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">{row.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            <strong>Note :</strong> "si assigné" = le module doit être coché dans la fiche utilisateur, et la structure doit lui être assignée.
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      {/* Formulaire d'ajout/édition */}
      {showAddForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex justify-between items-center">
              <span>{editingUser ? 'Modifier' : 'Ajouter'} un utilisateur</span>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="utilisateur@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Nom complet"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="viewer">Lecteur (lecture seule)</option>
                  <option value="staff">Équipe (créer/modifier, pas supprimer)</option>
                  <option value="structure_admin">Admin Structure (CRUD complet)</option>
                  <option value="admin">Admin Global</option>
                </select>
              </div>

              {formData.role !== 'admin' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Modules accessibles</label>
                  <div className="flex flex-wrap gap-3">
                    {ALL_MODULES.map(module => (
                      <label key={module} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.modules.includes(module)}
                          onChange={() => toggleModule(module)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{MODULE_LABELS[module]}</span>
                      </label>
                    ))}
                  </div>
                  {formData.modules.length === 0 && (
                    <p className="text-sm text-orange-600 mt-1">⚠️ Cet utilisateur n'aura accès à aucun module</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Module par défaut au démarrage</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={formData.defaultModule || ''}
                  onChange={e => setFormData({ ...formData, defaultModule: e.target.value || null })}>
                  <option value="">Premier module accessible</option>
                  {ALL_MODULES.map(m => (
                    <option key={m} value={m}>{MODULE_LABELS[m] || m}</option>
                  ))}
                </select>
              </div>

              {formData.role !== 'admin' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Structures assignées</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {structures.map(structure => (
                      <label key={structure.id} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.structures.includes(structure.id)}
                          onChange={() => toggleStructure(structure.id)}
                          className="w-4 h-4"
                        />
                        <Building size={16} className="text-gray-500" />
                        <span>{structure.name}</span>
                      </label>
                    ))}
                  </div>
                  {formData.structures.length === 0 && formData.role !== 'admin' && (
                    <p className="text-sm text-orange-600 mt-2">
                      ⚠️ Cet utilisateur n'aura accès à aucune structure
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition"
                >
                  <Save size={20} />
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100 transition"
                >
                  Annuler
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Liste des utilisateurs */}
      <Card>
        <CardHeader>
          <CardTitle>Utilisateurs ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">Nom</th>
                  <th className="text-left p-3 font-semibold">Email</th>
                  <th className="text-left p-3 font-semibold">Rôle</th>
                  <th className="text-left p-3 font-semibold hidden lg:table-cell">Modules</th>
                  <th className="text-left p-3 font-semibold">Structures</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Shield size={16} className="text-gray-400" />
                        {user.name}
                      </div>
                    </td>
                    <td className="p-3 text-gray-600">{user.email}</td>
                    <td className="p-3">{getRoleBadge(user.role)}</td>
                    <td className="p-3 hidden lg:table-cell">
                      {user.role === 'admin' ? (
                        <span className="text-sm text-gray-500 italic">Tous</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(user.modules) && user.modules.length > 0 ? (
                            user.modules.map(m => (
                              <span key={m} className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs rounded">
                                {MODULE_LABELS[m] || m}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-orange-600">Aucun</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {user.role === 'admin' ? (
                        <span className="text-sm text-gray-500 italic">Toutes</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {user.structures && user.structures.length > 0 ? (
                            user.structures.map(s => (
                              <span
                                key={s.structureId}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                              >
                                {s.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-orange-600">Aucune</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-2">
                        {impersonateEnabled && user.role !== 'admin' && (
                          <button
                            onClick={() => startImpersonating(user)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded transition"
                            title="Voir en tant que cet utilisateur"
                          >
                            <Eye size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 text-primary hover:bg-primary/5 rounded transition"
                          title="Modifier"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                          title="Supprimer"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Nouvel utilisateur"
        >
          <UserPlus size={24} />
        </button>
      )}
    </div>
  );
};

export default UserManagement;
